import { Button, DatePicker, Form, Input, InputNumber, message, Select, Table } from "antd";
import { collection, doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { PlannedExpenditureType } from "../typeDefs";

const statuses = ["Idea", "Planned", "Approved", "Purchased", "Deferred", "Cancelled"];
const priorities = ["Low", "Medium", "High", "Critical"];
const capabilities = [
  "Production",
  "Post-production",
  "Audio",
  "Lighting",
  "Editing",
  "Storage",
  "Web development",
  "Automation",
  "Studio operations",
  "Marketing",
];

const money = (value: number) => `$${(value || 0).toFixed(2)}`;

export const PlannedExpenditure = () => {
  const [items, setItems] = useState<PlannedExpenditureType[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "plannedExpenditure"), (snapshot) => {
      setItems(snapshot.docs.map((item) => ({ ...item.data(), id: item.id } as PlannedExpenditureType)));
    });

    return unsub;
  }, []);

  const savePlan = () => {
    form.validateFields().then((values) => {
      const id = nanoid();
      setDoc(doc(db, `plannedExpenditure/${id}`), {
        id,
        ...values,
        expectedPurchaseDate: values.expectedPurchaseDate?.toDate().getTime() || null,
        createdAt: Date.now(),
        isDeleted: false,
      } as PlannedExpenditureType)
        .then(() => {
          message.success("Planned expenditure saved.");
          form.resetFields();
        })
        .catch((err) => {
          console.error(err);
          message.error("Could not save planned expenditure.");
        });
    });
  };

  const activeItems = items.filter((item) => !item.isDeleted);
  const totalPlanned = activeItems
    .filter((item) => !["Purchased", "Cancelled"].includes(item.status))
    .reduce((total, item) => total + item.estimatedCost, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ fontWeight: "bold" }}>Total planned future spend: {money(totalPlanned)}</div>

      <Form form={form} layout="vertical">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Form.Item name="name" label="Asset / expense" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="estimatedCost" label="Estimated cost" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="Medium">
            <Select options={priorities.map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item name="capability" label="Capability gained" initialValue="Production">
            <Select options={capabilities.map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item name="expectedPurchaseDate" label="Expected purchase date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="Idea">
            <Select options={statuses.map((value) => ({ value, label: value }))} />
          </Form.Item>
        </div>
        <Form.Item name="businessReason" label="Business reason">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="fundingSource" label="Funding source">
          <Input />
        </Form.Item>
        <Button type="primary" onClick={savePlan}>Save planned expenditure</Button>
      </Form>

      <Button onClick={() => window.print()}>Print Planned Expenditure</Button>

      <Table
        size="small"
        pagination={{ pageSize: 10 }}
        dataSource={activeItems.map((item) => ({ ...item, key: item.id }))}
        columns={[
          { title: "Item", dataIndex: "name" },
          { title: "Cost", dataIndex: "estimatedCost", render: money },
          { title: "Priority", dataIndex: "priority" },
          { title: "Capability", dataIndex: "capability" },
          { title: "Reason", dataIndex: "businessReason" },
          { title: "Target Date", dataIndex: "expectedPurchaseDate", render: (value) => value ? new Date(value).toLocaleDateString("en-GB") : "-" },
          { title: "Status", dataIndex: "status" },
          {
            title: "Actions",
            render: (_value, record) => (
              <div style={{ display: "flex", gap: 8 }}>
                {record.status !== "Purchased" && (
                  <Button size="small" onClick={() => updateDoc(doc(db, `plannedExpenditure/${record.id}`), { status: "Purchased" })}>
                    Purchased
                  </Button>
                )}
                <Button size="small" danger onClick={() => updateDoc(doc(db, `plannedExpenditure/${record.id}`), { isDeleted: true })}>
                  Remove
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
